// src/i18n.js — SommSync translation module. No dependencies.
//
// Named tr(), not t(): t is the theme object in ~56 App.js components.
//
//   tr('home.title')                        → plain string
//   tr('chat.greeting', { name: 'Ann' })    → "Hi {name}" slots, inserted as-is
//   tr('list.count', { count: 12 })         → plural form chosen by the browser's
//                                             Intl.PluralRules for the language
//   "{count, number}" in a string           → locale-formatted number (1 200 in fr-CA)
//
// Plural entries are objects: { zero?, one, few?, many?, other }. `other` is required.
// `zero` is optional and used only for an exact 0 ("No wines yet").
// Any key missing in the active language falls back to English; a key missing
// everywhere returns the key itself and warns once, so a gap is visible, never blank.

import en from './locales/en';

const FALLBACK = 'en';

// ─── Loading languages on demand (Oct 30, 2026) ────────────────────────────
// English is built in. Every other language is its own file, fetched the first
// time it is needed (webpack makes each import() a separate chunk), so the app
// does not grow with the catalogue. Until a file arrives, tr() answers in
// English; onLanguageLoaded() listeners re-render when it lands.
//
// Quebec French and Mexican Spanish also answer for every other French and
// Spanish request (fr, fr-FR, es-ES…), and Simplified Chinese for every Chinese
// one, through ALIASES. Add a language: a loader here, its file in src/locales/,
// GUEST_LANGUAGES in App.js, and the server tables (see CLAUDE.md).
const LOADERS = {
  'fr-CA': () => import('./locales/fr-CA'),
  'es-MX': () => import('./locales/es-MX'),
  de: () => import('./locales/de'),
  it: () => import('./locales/it'),
  pl: () => import('./locales/pl'),
  cs: () => import('./locales/cs'),
  sk: () => import('./locales/sk'),
  hu: () => import('./locales/hu'),
  ro: () => import('./locales/ro'),
  ru: () => import('./locales/ru'),
  uk: () => import('./locales/uk'),
  el: () => import('./locales/el'),
  ja: () => import('./locales/ja'),
  ko: () => import('./locales/ko'),
  'zh-CN': () => import('./locales/zh-CN'),
};
const ALIASES = { fr: 'fr-CA', es: 'es-MX', zh: 'zh-CN' };
const LOCALES = { en };
let current = FALLBACK;

const warned = new Set();
const pluralRules = {};
const numberFormats = {};
const pending = {};
const listeners = new Set();

export function registerLocale(code, strings) {
  LOCALES[code] = strings;
}

// 'fr-CA' → 'fr-CA', else 'fr', else English - among the files loaded so far.
function resolveCode(code) {
  if (!code) return FALLBACK;
  if (LOCALES[code]) return code;
  const base = String(code).split('-')[0];
  return LOCALES[base] ? base : FALLBACK;
}

// The file that serves a code: exact, then base, then alias ('fr-FR' → 'fr-CA').
function fileFor(code) {
  if (!code) return null;
  if (LOADERS[code]) return code;
  const base = String(code).split('-')[0].toLowerCase();
  if (LOADERS[base]) return base;
  return ALIASES[base] || null;
}

// What was ASKED for, before falling back to a language we have screens for.
// Sent to the server with pairing and chat requests: a guest who chose French
// gets French prose from the sommelier even while a screen string falls back to
// English. getLanguage() is what the screens actually show.
let requested = FALLBACK;

function setHtmlLang() {
  // <html lang> follows what is shown: screen readers pronounce it correctly, and
  // a browser does not offer to "translate" a page that is already French.
  if (typeof document !== 'undefined' && document.documentElement) document.documentElement.lang = current;
}

// Fetch a language's file (once). Resolves when it is usable; a failed fetch
// (offline tablet) resolves too, leaving English, and can be retried later.
export function loadLanguage(code) {
  const file = fileFor(code);
  if (!file || LOCALES[file]) return Promise.resolve(current);
  if (!pending[file]) {
    pending[file] = LOADERS[file]()
      .then(mod => {
        const strings = mod.default || mod;
        LOCALES[file] = strings;
        Object.entries(ALIASES).forEach(([alias, target]) => { if (target === file) LOCALES[alias] = strings; });
        current = resolveCode(requested);
        setHtmlLang();
        listeners.forEach(cb => { try { cb(current); } catch (e) { /* listener's problem */ } });
        return current;
      })
      .catch(err => {
        delete pending[file];
        console.warn(`[i18n] could not load ${file}:`, err && err.message);
        return current;
      });
  }
  return pending[file];
}

// Called when a language file arrives; returns an unsubscribe function.
export function onLanguageLoaded(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function setLanguage(code) {
  requested = code ? String(code) : FALLBACK;
  current = resolveCode(code);
  setHtmlLang();
  if (fileFor(code) && !LOCALES[fileFor(code)]) loadLanguage(code);
  return current;
}

export function getLanguage() {
  return current;
}

export function getRequestedLanguage() {
  return requested;
}

function lookup(strings, key) {
  return key.split('.').reduce((node, part) => (node == null ? undefined : node[part]), strings);
}

function pluralCategory(lang, n) {
  if (!pluralRules[lang]) pluralRules[lang] = new Intl.PluralRules(lang);
  return pluralRules[lang].select(n);
}

function formatNumber(lang, n) {
  if (!numberFormats[lang]) numberFormats[lang] = new Intl.NumberFormat(lang);
  return numberFormats[lang].format(n);
}

function interpolate(str, vars, lang) {
  return str.replace(/\{(\w+)(,\s*number)?\}/g, (whole, name, asNumber) => {
    if (!(name in vars) || vars[name] == null) return whole;
    const v = vars[name];
    return asNumber && typeof v === 'number' ? formatNumber(lang, v) : String(v);
  });
}

function warnOnce(key) {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(`[i18n] missing key: ${key}`);
}

export function tr(key, vars) {
  let lang = current;
  let entry = lookup(LOCALES[lang], key);
  if (entry === undefined && lang !== FALLBACK) {
    lang = FALLBACK; // plural rules must match the language actually shown
    entry = lookup(LOCALES[FALLBACK], key);
  }

  if (entry && typeof entry === 'object') {
    const n = vars && typeof vars.count === 'number' ? vars.count : undefined;
    entry = (n === 0 && entry.zero) || (n !== undefined && entry[pluralCategory(lang, n)]) || entry.other;
  }

  if (typeof entry !== 'string') {
    warnOnce(key);
    return key;
  }
  return vars ? interpolate(entry, vars, lang) : entry;
}
